import React, { memo, useCallback, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import ReactGA from 'react-ga4'

import { useTimer } from 'react-timer-hook'
import T2CForm from './components/T2CForm'
import InfoBlock from './components/InfoBlock'
import { Step } from '../../enums/enums'
import OtpForm from './components/OtpForm'
import { t2cConfirm, t2cInit, t2cSendOtp } from '../../utils/api'
import { InputProps } from '../../utils/enum'
import SuccesForm from '../../components/SuccesForm'
import ErrorForm from '../../components/ErrorForm'
import { T2CErrorsValidation } from '../../utils/errors'
import { maxTime } from '../../utils/const'

const T2C: React.FC = () => {
  const [flow, setFlow] = useState<Step>(Step.main)
  const [otp, setOtp] = useState<string>('')
  const [initInfo, setInitInfo] = useState<any>({})
  const [uiError, setUiError] = useState<string>('')
  const [operationId, setOperationId] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [otpError, setOtpError] = useState<string>('')
  const [additionalInfo, setAdditionalInfo] = useState<any>({})
  const [showOtp, setShowOtp] = useState<boolean>(false)
  const [searchParams] = useSearchParams()
  const [otpResponse, setOtpResponse] = useState<any>({})
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(false)
  const ref = searchParams.get('ref')
  const navigate = useNavigate()
  useEffect(() => {
    sessionStorage.setItem('t2cSessionId', operationId)
  }, [operationId])
  const exp = new Date(
    new Date().getTime() + 1000 * (otpResponse?.otpInfo?.cooldownSeconds || maxTime),
  )
  const timer = useTimer({
    expiryTimestamp: exp,
  })
  const handleInitError = (data: any) => {
    ReactGA.event({
      category: 'T2C',
      action: 'T2CInitError',
      label: 'T2CInitError',
      value: data?.errors,
    })

    if (data?.showMessage4Ui) {
      setUiError(data?.message4Ui)
    } else {
      data?.errors?.forEach((error: any) => {
        setUiError(T2CErrorsValidation(error.errorCode))
      })
    }
  }

  const handleSubmit = useCallback(
    (values: any) => {
      const sendOtpAndSetState = () => {
        t2cSendOtp(false, operationId).then((r) => {
          if (r.ok) {
            setOtpResponse(r?.result)
            timer.restart(
              new Date(new Date().getTime() + 1000 * (r.result?.cooldownSeconds || maxTime)),
            )
            setFlow(Step.otp)
          } else {
            setFlow(Step.error)
          }
        })
      }
      const handleInitSuccess = (data: any) => {
        setInitInfo(data)
        setIsLoading(false)
        if (data?.ok === true) {
          ReactGA.event({
            category: 'T2C',
            action: 'T2CInit',
            label: 'T2CInit',
            value: values.provider,
          })
          if (data?.result?.customerInputFields?.length > 0) {
            setShowOtp(true)
            setFlow(Step.otp)
          } else {
            sendOtpAndSetState()
          }
          setAdditionalInfo(data?.result?.customerInputFields)
        } else {
          handleInitError(data)
        }
      }
      setIsLoading(true)
      t2cInit(operationId, values, ref)
        .then(handleInitSuccess)
        .catch((e) => {
          ReactGA.event({
            category: 'T2C',
            action: 'T2CInitError',
            label: 'T2CInitError',
            value: e,
          })
          setIsLoading(false)
        })
    },
    [operationId],
  )

  const formik = useFormik({
    validationSchema: Yup.object({
      provider: Yup.number()
        .moreThan(0, 'p2p-page.error.required')
        .required('p2p-page.error.required'),
      code: Yup.string()
        .when(['provider'], (provider: number) => {
          if (provider === 1) {
            return Yup.string()
              .max(16, 'error.max16')
              .min(4, 'error.min4')
              .matches(/^[0-9]+$/, 'p2p-page.error.error')
              .required('p2p-page.error.required')
          }
          if (provider === 3) {
            return Yup.string()
              .max(8, 'error.max8')
              .min(8, 'error.min8')
              .matches(/^[0-9]+$/, 'p2p-page.error.error')
              .required('p2p-page.error.required')
          }
          if (provider === 5) {
            return Yup.string()
              .max(17, 'error.max17')
              .min(11, 'error.min11')
              .matches(/^[0-9]+$/, 'p2p-page.error.error')
              .required('p2p-page.error.required')
          }
          if (provider === 6) {
            return Yup.string()
              .max(11, 'error.max11')
              .min(11, 'error.min11')
              .matches(/^[0-9]+$/, 'p2p-page.error.error')
              .required('p2p-page.error.required')
          }
          if (provider === 7) {
            return Yup.string()
              .max(10, 'error.max10')
              .min(10, 'error.min10')
              .matches(/^[0-9]+$/, 'p2p-page.error.error')
              .required('p2p-page.error.required')
          }
          if (provider === 10 || provider === 1009) {
            return Yup.string()
              .max(16, 'error.max16')
              .min(1, 'error.min1')
              .required('p2p-page.error.required')
          }
          if (provider === 11) {
            return Yup.string()
              .max(16, 'error.max16')
              .min(7, 'error.min7')
              .required('p2p-page.error.required')
          }
          if (provider === 1010) {
            return Yup.string()
              .max(16, 'error.max16')
              .min(5, 'error.min5')
              .required('p2p-page.error.required')
          }
          return Yup.string()
            .max(16, 'error.max16')
            .min(1, 'error.min1')
            .required('p2p-page.error.required')
        })
        .required('p2p-page.error.required'),
      amount: Yup.number()
        .min(1)
        .typeError('error.amount')
        .nullable(true)
        .required('p2p-page.error.required'),
      currency: Yup.number().required('p2p-page.error.required'),
      idnp: Yup.string()
        .min(5)
        .matches(/^[0-9]+$/, 'p2p-page.error.error')
        .required('p2p-page.error.required'),
      cardNumber: Yup.string()
        .matches(/^[0-9•]+$/, 'p2p-page.error.error')
        .required('p2p-page.error.required'),
      agree: Yup.boolean().required('p2p-page.error.required'),
    }),
    initialValues: {
      provider: 0,
      code: '',
      amount: '',
      currency: 1,
      idnp: '',
      cardNumber: '',
      agree: false,
    },
    onSubmit: handleSubmit,
  })

  const handleNext = useCallback(
    (values: any) => {
      setIsLoading(true)
      formik.resetForm()
      setUiError('')
      setOtpError('')
      t2cConfirm(operationId, otp, values)
        .then((data: any) => {
          setIsLoading(false)
          if (data.ok) {
            ReactGA.event({
              category: 'T2C',
              action: 'T2CConfirmSuccess',
              label: 'T2CConfirmSuccess',
              value: values.provider,
            })
            setUiError('')
            setFlow(Step.success)
          } else if (!data.ok) {
            if (data?.errors) {
              ReactGA.event({
                category: 'T2C',
                action: 'T2CConfirmError',
                label: 'T2CConfirmError',
                value: data?.errors,
              })
              data.errors.forEach((error: any) => {
                if (error.errorCode === 'MaibPay.T2C-IMTIS-25') {
                  if (data?.result?.validateAttempts === 0) {
                    navigate('/error', { state: { location: window.location.pathname } })
                  } else {
                    setOtpError('t2c-page.otp.error')
                    setOtp('')
                  }
                }
                if (error.errorCode === 'maib.otp-7') {
                  if (data?.result?.validateAttempts === 0) {
                    navigate('/error', { state: { location: window.location.pathname } })
                  } else {
                    setOtpError('t2c-page.otp.error')
                    setOtp('')
                  }
                } else {
                  setFlow(Step.error)
                }
              })
            }
          }
        })
        .catch((e: any) => {
          ReactGA.event({
            category: 'T2C',
            action: 'T2CConfirmAxios',
            label: 'T2CConfirmAxios',
            value: e,
          })
          setIsLoading(false)
          setFlow(Step.error)
        })
    },
    [otp, operationId],
  )

  const handleResend = async () => {
    setIsLoading(true)
    ReactGA.event({
      category: 'T2C',
      action: 'T2CResend',
      label: 'T2CRResend',
    })
    setOtpError('')
    await t2cSendOtp(true, operationId).then((res) => {
      if (res.ok) {
        if (res.result?.resendAttempts === 0) {
          setIsLoading(false)
          setIsResendDisabled(true)
        } else {
          setOtpResponse(res?.result)
          setIsLoading(false)
          timer.restart(
            new Date(new Date().getTime() + 1000 * (res.result?.cooldownSeconds || maxTime)),
          )
        }
      }
      if (res.ok === false) {
        setIsLoading(false)
        setOtpError(res?.message4Ui)
      }
    })
  }

  const handleAdditionalInfo = async () => {
    t2cSendOtp(false, operationId).then((r) => {
      if (r.ok) {
        setOtpResponse(r?.result)
        timer.restart(
          new Date(new Date().getTime() + 1000 * (r.result?.cooldownSeconds || maxTime)),
        )
        setShowOtp(false)
      } else {
        setFlow(Step.error)
      }
    })
  }

  const renderBlocks = () => {
    switch (flow) {
      case Step.main:
        return (
          <div>
            <div className='relative flex-1'>
              {isLoading && (
                <div className='absolute top-0 left-0 w-full h-full bg-white bg-opacity-50 z-50 flex items-center justify-center'>
                  <CircularProgress />
                </div>
              )}
              <div className='sm:py-16 py-8 container mx-auto flex flex-col px-auto flex-auto items-center '>
                <T2CForm
                  InputProps={InputProps(formik.values.provider)}
                  setOperationId={setOperationId}
                  error={uiError}
                  isLoading={isLoading}
                  showMessage4Ui={initInfo?.showMessage4Ui || false}
                  formik={formik}
                  className='w-full sm:w-4/6 md:w-4/6 lg:w-5/12 xl:w-4/12  px-4'
                />
              </div>
            </div>
            <InfoBlock />
          </div>
        )
      case Step.otp:
        return (
          <div className='relative flex-1 bg-white'>
            {isLoading && (
              <div className='absolute top-0 left-0 w-full h-full bg-white bg-opacity-50 z-50 flex items-center justify-center'>
                <CircularProgress />
              </div>
            )}
            <OtpForm
              setOtp={setOtp}
              isLoading={isLoading}
              otp={otp}
              otpResponse={otpResponse}
              isResendDisabled={isResendDisabled}
              handleNext={handleNext}
              initInfo={initInfo}
              additionalInfo={additionalInfo}
              showOtp={showOtp}
              handleAdditionalInfo={handleAdditionalInfo}
              handleResend={handleResend}
              otpError={otpError}
              timer={timer}
              provider={formik.values.provider}
            />
          </div>
        )
      case Step.success:
        return (
          <SuccesForm
            moneygramOperationId={operationId}
            handleSuccess={() => {
              setUiError('')
              window.scroll({ top: 0, left: 0, behavior: 'smooth' })
              navigate('/', { replace: true })
            }}
          />
        )
      case Step.error:
        return (
          <ErrorForm
            handleSuccess={() => {
              setUiError('')
              window.scroll({ top: 0, left: 0, behavior: 'smooth' })
              setFlow(Step.main)
            }}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className='flex flex-col flex-1 min-h-full mx-auto w-full bg-mainBackground relative'>
      {renderBlocks()}
    </div>
  )
}

export default memo(T2C)
